import {PageHeader} from '../ui/PageHeader';
import {ResponsiveTable} from '../ui/ResponsiveTable';
import {MailMeetingEditor} from './MailMeetingEditor';
import { DateOnlyInput } from "../components/DateOnlyInput";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { api, ApiError } from "../api";
import { ErrorState } from "../ui/State";
import {
  formatDateTime,
  formatZonedDateTime,
  londonDateTimeInputInstant,
  timestampDateOnlyValue,
} from "../dates";
type Mailbox = { workspace_available?:boolean; id: string; display_name: string; channel_address: string };
type Folder = {
  folder_path: string;
  folder_kind: string;
  backfill_status: string;
  excluded_by_user: boolean;
  messages_failed: number;
};
type Workspace = {
  account: Mailbox & {
    sync_status: string;
    sync_interval_seconds: number;
    last_sync_completed_at?: string;
    next_sync_at?: string;
    historical_backfill_status: string;
    can_send: boolean;
  };
  signature: string;
  folders: Folder[];
  history?: {
    status: string;
    messages_seen: number;
    messages_ingested: number;
    duplicates_skipped: number;
    messages_failed: number;
    folders_completed: number;
    folders_total: number;
  } | null;
  pendingActions?: Array<{ id: string; kind: string; status: string }>;
  externalActionsEnabled: boolean;
  calendarPermitted: boolean;
};
type Event = {
  id: string;
  subject: string;
  changeKey: string;
  type?:string;seriesMasterId?:string;
  isOrganizer: boolean;
  isCancelled: boolean;
  start: { dateTime: string; timeZone: string };
  end: { dateTime: string; timeZone: string };
  responseStatus?: { response: string };
  organizer?: { emailAddress?: { address?: string } };
};
export function MailWorkspace() {
  const [accounts, setAccounts] = useState<Mailbox[]>([]),
    [accountsLoaded,setAccountsLoaded]=useState(false),
    [id, setId] = useState(""),
    [workspace, setWorkspace] = useState<Workspace>(),
    [events, setEvents] = useState<Event[]>(),
    [hasMore, setHasMore] = useState(false),
    [error, setError] = useState<unknown>(),
    [notice, setNotice] = useState(""),
    [busy, setBusy] = useState(false),
    [signature, setSignature] = useState(""),
    [auto, setAuto] = useState<string>(),
    [from, setFrom] = useState(timestampDateOnlyValue(new Date()));
  const generation = useRef(0),
    command = useRef<string | undefined>(undefined),
    root = `/api/communications/accounts/${id}/workspace`;
  const period = () => {
    const start = new Date(from + "T00:00:00Z"),
      end = new Date(start);
    end.setUTCDate(end.getUTCDate() + 7);
    return `from=${londonDateTimeInputInstant(from + "T00:00")}&to=${londonDateTimeInputInstant(end.toISOString().slice(0, 10) + "T00:00")}`;
  };
  useEffect(() => {
    void api<Mailbox[]>("/api/communications/accounts")
      .then((list) => {
        const personal=list.filter(account=>account.workspace_available!==false);
        setAccounts(personal);setAccountsLoaded(true);
        setId(personal[0]?.id ?? "");
      })
      .catch(setError);
  }, []);
  useEffect(() => {
    if (!id) return;
    const n = ++generation.current;
    setWorkspace(undefined);
    setEvents(undefined);
    setAuto(undefined);
    setError(undefined);
    setNotice("");
    command.current = undefined;
    void api<Workspace>(root)
      .then((w) => {
        if (n === generation.current) {
          setWorkspace(w);
          setSignature(w.signature);
        }
      })
      .catch((e) => {
        if (n === generation.current) setError(e);
      });
  }, [id]);
  async function action(work: () => Promise<unknown>, message: string) {
    if (busy) return;
    setBusy(true);
    setError(undefined);
    setNotice("");
    try {
      await work();
      setNotice(message);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  async function calendar() {
    const n = generation.current;
    const r = await api<{ items: Event[]; hasMore: boolean }>(
      root + "/calendar?" + period(),
    );
    if (n === generation.current) {
      setEvents(r.items);
      setHasMore(r.hasMore);
    }
  }
  async function external(path: string, body: Record<string, unknown>) {
    command.current ??= crypto.randomUUID();
    try {
      const result = await api(root + path, {
        method: "POST",
        body: JSON.stringify({
          ...body,
          commandId: command.current,
          confirmation: "CONFIRM",
        }),
      });
      command.current = undefined;
      return result;
    } catch (e) {
      if (e instanceof ApiError && e.statusCode < 500)
        command.current = undefined;
      throw e;
    }
  }
  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget,
      data = new FormData(form);
    await action(async () => {
      await external("/calendar/create", {
        subject: data.get("subject"),
        body: data.get("message"),
        start: londonDateTimeInputInstant(String(data.get("start"))),
        end: londonDateTimeInputInstant(String(data.get("end"))),
        attendees: String(data.get("attendees") ?? "")
          .split(",")
          .map((a) => a.trim())
          .filter(Boolean),
        location: data.get("location"),
        ...(data.get('frequency')==='none'?{}:{recurrence:{frequency:data.get('frequency'),interval:Number(data.get('interval')),occurrences:Number(data.get('occurrences'))}}),
      });
      form.reset();
      await calendar();
    }, "Microsoft accepted the calendar invitation.");
  }
  return (
    <div className="mailbox-tools">
      <PageHeader title="My mailbox tools" context="Check email coverage, manage your signature and use your Microsoft calendar." secondaryActions={[{kind:'link',label:'Back to Mail',to:'/communications'}]}/>
      {accounts.length>0&&<label className="mailbox-workspace-selector">
        Mailbox
        <select
          value={id}
          disabled={busy}
          onChange={(e) => setId(e.target.value)}
        >
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.display_name} · {a.channel_address}
            </option>
          ))}
        </select>
      </label>}
      {accountsLoaded&&accounts.length===0&&<p role="status">No personal mailboxes are available. An authorised mailbox administrator can review your access. Configuration access alone does not allow personal mailbox tools.</p>}
      {notice && <p role="status">{notice}</p>}
      {Boolean(error) && <ErrorState error={error} />}
      {workspace && (
        <>
          <section className="panel">
            <h2>Email readiness</h2>
            <button
              className="secondary"
              disabled={busy}
              onClick={() =>
                void action(
                  async () => setWorkspace(await api<Workspace>(root)),
                  "Readiness refreshed.",
                )
              }
            >
              Refresh readiness
            </button>
            {Boolean(workspace.pendingActions?.length) && (
              <p role="status">
                A previous Microsoft action has an unconfirmed outcome. Review
                it with an administrator before repeating the action or
                restricting Outlook.
              </p>
            )}
            <p>
              Connection: {workspace.account.sync_status}. Last checked:{" "}
              {formatDateTime(
                workspace.account.last_sync_completed_at,
                "Not yet checked",
              )}
              . Next check:{" "}
              {formatDateTime(workspace.account.next_sync_at, "Not scheduled")}.
            </p>
            <button
              disabled={busy}
              onClick={() =>
                void action(async () => {
                  await api(`/api/communications/accounts/${id}/sync`, {
                    method: "POST",
                  });
                  setWorkspace(await api<Workspace>(root));
                }, "Mailbox check completed.")
              }
            >
              Check for new mail
            </button>
            <p>
              Automatic checks: every{" "}
              {workspace.account.sync_interval_seconds / 60} minutes.
            </p>
            <button
              disabled={busy}
              onClick={() =>
                void action(async () => {
                  await api(root + "/polling", {
                    method: "PUT",
                    body: JSON.stringify({ seconds: 60 }),
                  });
                  setWorkspace(await api<Workspace>(root));
                }, "Automatic checks are now scheduled every minute.")
              }
            >
              Check mail every minute
            </button>
            <p>
              Latest inventory: {workspace.account.historical_backfill_status}.
              {workspace.history
                ? ` Historical import: ${workspace.history.status}; ${workspace.history.folders_completed} of ${workspace.history.folders_total} folders completed, ${workspace.history.messages_seen} messages checked, ${workspace.history.messages_ingested} added, ${workspace.history.duplicates_skipped} already held, ${workspace.history.messages_failed} failure attempts recorded.`
                : " No historical import receipt is available."}
              Completion covers the selected folders. Failure counts retain
              earlier attempts after a successful retry. Review exclusions and
              original attachment coverage before switching.
            </p>
            <details>
              <summary>Review history coverage and exclusions</summary>

<ResponsiveTable>
                <thead>
                  <tr>
                    <th>Folder</th>
                    <th>Import</th>
                    <th>Recorded failure attempts</th>
                  </tr>
                </thead>
                <tbody>
                  {workspace.folders.map((f) => (
                    <tr key={f.folder_path}>
                      <td>{f.folder_path}</td>
                      <td>
                        {f.excluded_by_user
                          ? "Excluded by mailbox policy"
                          : f.backfill_status}
                      </td>
                      <td>{f.messages_failed}</td>
                    </tr>
                  ))}
                </tbody>
              </ResponsiveTable>

            </details>
            <p>
              Older mail, Deleted Items, Junk and excluded folders must be
              reviewed before Outlook is restricted.{" "}
              <Link to="/admin/mail/mailboxes">
                Review mailbox import settings
              </Link>
            </p>
          </section>
          <form
            className="panel"
            onSubmit={(e) => {
              e.preventDefault();
              void action(
                () =>
                  api(root + "/signature", {
                    method: "PUT",
                    body: JSON.stringify({ signature }),
                  }),
                "Your signature was saved. Use Insert my signature when composing.",
              );
            }}
          >
            <h2>My signature for this mailbox</h2>
            <label>
              Signature
              <textarea
                value={signature}
                maxLength={4000}
                rows={5}
                onChange={(e) => setSignature(e.target.value)}
              />
            </label>
            <button disabled={busy}>Save signature</button>
            <p>
              This signature belongs to you. It is inserted only when you choose
              it in the email composer.
            </p>
          </form>
          <section className="panel">
            <h2>Mailbox meetings</h2>
            <p>
              Manage the selected mailbox’s meetings here in BlockGenie. Invitations
              and updates remain connected to its Microsoft calendar.
            </p>
            <label>
              Week starting
              <DateOnlyInput required value={from} onChange={setFrom} />
            </label>
            <button
              disabled={busy || !from}
              onClick={() =>
                void action(calendar, "Microsoft calendar refreshed.")
              }
            >
              Load meetings
            </button>
            {hasMore && (
              <p>
                More than 100 entries occur in this week. Review a quieter week
                before relying on this list as complete.
              </p>
            )}
            {events?.map((event) => (
              <article key={event.id}>
                <h3>{event.subject}</h3>
                <p>
                  {formatZonedDateTime(
                    event.start.dateTime.replace(/Z$/, "") + "Z",
                    "Europe/London",
                  )}{" "}
                  to{" "}
                  {formatZonedDateTime(
                    event.end.dateTime.replace(/Z$/, "") + "Z",
                    "Europe/London",
                  )}{" "}
                  ·{" "}
                  {event.isCancelled
                    ? "Cancelled"
                    : event.responseStatus?.response}
                </p>
                <p>Organiser: {event.organizer?.emailAddress?.address}</p>
                {event.isOrganizer&&!event.isCancelled&&workspace.calendarPermitted&&workspace.account.can_send&&
                  <MailMeetingEditor event={event} root={root} busy={busy} enabled={workspace.externalActionsEnabled} external={external} refresh={calendar}/>}
                {!event.isOrganizer &&
                  !event.isCancelled &&
                  workspace.calendarPermitted &&
                  workspace.account.can_send && (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const data = new FormData(e.currentTarget);
                        void action(async () => {
                          await external("/calendar/respond", {
                            eventId: event.id,
                            changeKey: event.changeKey,
                            decision: data.get("decision"),
                            comment: data.get("comment"),
                          });
                          await calendar();
                        }, "Microsoft accepted your response.");
                      }}
                    >
                      <label>
                        Response
                        <select name="decision">
                          <option value="accept">Accept</option>
                          <option value="tentativelyAccept">Tentative</option>
                          <option value="decline">Decline</option>
                        </select>
                      </label>
                      <label>
                        Comment
                        <input name="comment" maxLength={4000} />
                      </label>
                      <label>
                        <input type="checkbox" required />
                        Send this response to the organiser
                      </label>
                      <button
                        disabled={busy || !workspace.externalActionsEnabled}
                      >
                        Confirm and send response
                      </button>
                    </form>
                  )}
              </article>
            ))}
            {workspace.calendarPermitted && workspace.account.can_send && (
              <details>
                <summary>Create an appointment or invitation</summary>
                <form onSubmit={create}>
                  <label>
                    Subject
                    <input name="subject" required maxLength={500} />
                  </label>
                  <label>
                    Starts (Europe/London)
                    <input name="start" type="datetime-local" required />
                  </label>
                  <label>
                    Ends (Europe/London)
                    <input name="end" type="datetime-local" required />
                  </label>
                  <label>Repeat<select name="frequency"><option value="none">Does not repeat</option><option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option><option value="yearly">Yearly</option></select></label>
                  <label>Repeat every<input name="interval" type="number" min={1} max={99} defaultValue={1}/></label>
                  <label>Number of meetings<input name="occurrences" type="number" min={2} max={999} defaultValue={12}/></label>
                  <label>
                    Invite these email addresses
                    <input
                      name="attendees"
                      placeholder="Separate addresses with commas"
                    />
                  </label>
                  <label>
                    Location
                    <input name="location" maxLength={500} />
                  </label>
                  <label>
                    Message
                    <textarea name="message" maxLength={10000} />
                  </label>
                  <label>
                    <input type="checkbox" required />
                    Create this Microsoft appointment and send invitations to
                    the listed people
                  </label>
                  <button disabled={busy || !workspace.externalActionsEnabled}>
                    Confirm appointment
                  </button>
                </form>
              </details>
            )}
          </section>
          <section className="panel">
            <h2>Out-of-office replies</h2>
            <p>
              Turning these on lets Microsoft send automatic replies.
              BlockGenie’s engine sending controls are separate.
            </p>
            <button
              disabled={busy}
              onClick={() =>
                void action(async () => {
                  const r = await api<{ status: string }>(
                    root + "/automatic-replies",
                  );
                  setAuto(r.status);
                  command.current = undefined;
                }, "Out-of-office status refreshed.")
              }
            >
              Check current status
            </button>
            {auto && <p>Microsoft status: {auto}</p>}
            {workspace.account.can_send && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const d = new FormData(e.currentTarget);
                  void action(async () => {
                    await external("/automatic-replies", {
                      status: d.get("status"),
                      ...(d.get("status") === "scheduled"
                        ? {
                            start: londonDateTimeInputInstant(
                              String(d.get("start")),
                            ),
                            end: londonDateTimeInputInstant(
                              String(d.get("end")),
                            ),
                          }
                        : {}),
                      internalReplyMessage: d.get("internalReplyMessage"),
                      externalReplyMessage: d.get("externalReplyMessage"),
                      externalAudience: d.get("externalAudience"),
                    });
                    setAuto(undefined);
                  }, "Microsoft accepted the out-of-office settings. Check current status to verify.");
                }}
              >
                <label>
                  Mode
                  <select name="status">
                    <option value="disabled">Off</option>
                    <option value="scheduled">Scheduled</option>
                  </select>
                </label>
                <label>
                  Starts (Europe/London)
                  <input name="start" type="datetime-local" />
                </label>
                <label>
                  Ends (Europe/London)
                  <input name="end" type="datetime-local" />
                </label>
                <label>
                  Reply to colleagues
                  <textarea name="internalReplyMessage" maxLength={4000} />
                </label>
                <label>
                  External audience
                  <select name="externalAudience">
                    <option value="none">No external replies</option>
                    <option value="contactsOnly">Contacts only</option>
                    <option value="all">All external senders</option>
                  </select>
                </label>
                <label>
                  Reply to external senders
                  <textarea name="externalReplyMessage" maxLength={4000} />
                </label>
                <label>
                  <input type="checkbox" required />
                  Apply these Microsoft automatic-reply settings
                </label>
                <button disabled={busy || !workspace.externalActionsEnabled}>
                  Confirm out-of-office settings
                </button>
              </form>
            )}
          </section>
        </>
      )}
    </div>
  );
}
